/**
 * DEFCAT P6 — Versions tab. Version switcher (Draft · published history),
 * "Edit" (branch a draft from the current published version), and "Publish"
 * (opens the diff modal → confirm → POST publish).
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import type { CatalogueVersionSummary } from "@aeriosiq/types";
import { useBranchVersionMutation } from "./taxonomyQueries.js";
import { PublishDiffDialog } from "./PublishDiffDialog.js";

export interface VersionsTabProps {
  catalogueId: string;
  versions: CatalogueVersionSummary[];
  selectedVersionId: string | null;
  onSelectVersion: (versionId: string) => void;
  /** Role-gated: branch a draft / edit (tenant_admin + expert_analyst). */
  canBranch: boolean;
  /** Role-gated: publish a draft → live immutable version (tenant_admin). */
  canPublish: boolean;
}

export function VersionsTab({
  catalogueId,
  versions,
  selectedVersionId,
  onSelectVersion,
  canBranch,
  canPublish,
}: VersionsTabProps): ReactElement {
  const branch = useBranchVersionMutation(catalogueId);
  const [publishFor, setPublishFor] = useState<string | null>(null);

  const draft = versions.find((v) => v.status === "draft") ?? null;

  function handleBranch(): void {
    branch.mutate(undefined, {
      onSuccess: (created) => {
        onSelectVersion(created.versionId);
      },
    });
  }

  return (
    <Box sx={{ p: 1, maxWidth: 720 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          {draft === null ? (
            canBranch ? (
              <Button
                variant="contained"
                onClick={handleBranch}
                disabled={branch.isPending}
              >
                Edit (branch a draft)
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                You don&apos;t have permission to edit this catalogue.
              </Typography>
            )
          ) : canPublish ? (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                setPublishFor(draft.versionId);
              }}
            >
              Publish draft
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              A draft is open. Only a tenant admin can publish it.
            </Typography>
          )}
        </Stack>

        {branch.isError ? (
          <Alert severity="error">
            Could not branch a draft (a draft may already be open).
          </Alert>
        ) : null}

        <Typography variant="subtitle2">Version history</Typography>
        <List>
          {versions.map((v) => {
            const selected = v.versionId === selectedVersionId;
            return (
              <ListItemButton
                key={v.versionId}
                selected={selected}
                onClick={() => {
                  onSelectVersion(v.versionId);
                }}
              >
                <ListItemText
                  primary={`v${String(v.versionNo)}`}
                  secondary={
                    v.publishedAt != null
                      ? `published ${new Date(v.publishedAt).toLocaleString()}`
                      : (v.notes ?? undefined)
                  }
                />
                <Chip
                  size="small"
                  label={v.status}
                  color={
                    v.status === "draft"
                      ? "warning"
                      : v.status === "published"
                        ? "success"
                        : "default"
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </Stack>

      {publishFor !== null ? (
        <PublishDiffDialog
          open
          catalogueId={catalogueId}
          versionId={publishFor}
          onClose={() => {
            setPublishFor(null);
          }}
          onPublished={() => {
            setPublishFor(null);
          }}
        />
      ) : null}
    </Box>
  );
}
